import React, { useEffect, useMemo, useRef, useState } from "react";
import { FiX } from "react-icons/fi";
import { client } from "../Clients";

type TagsInputProps = {
	label?: string;
	/** Stored contract: a comma-separated string ("a, b, c"). */
	value: string;
	onChange: (next: string) => void;
	/** Content type whose existing values feed the suggestion dropdown. No key → no suggestions. */
	contentTypeKey?: string;
	/** Field the suggestions are read from; defaults to "tags". */
	fieldKey?: string;
	disabled?: boolean;
	hasError?: boolean;
};

const splitTags = (value?: string | null) =>
	(value ?? "")
		.split(",")
		.map((tag) => tag.trim())
		.filter(Boolean);

const joinTags = (tags: string[]) => tags.join(", ");

/**
 * Tag field editor: the stored value stays a comma-separated string, rendered as removable chips.
 * A tag is added on Enter, on typing a comma, on blur, or by clicking a suggestion. Suggestions
 * are the values already used on other entries of the same content type (fetched once on mount);
 * free text is always allowed : the dropdown is a helper, not a constraint.
 */
const TagsInput: React.FC<TagsInputProps> = ({
	label,
	value,
	onChange,
	contentTypeKey,
	fieldKey,
	disabled = false,
	hasError = false
}) => {
	const [text, setText] = useState("");
	const [focused, setFocused] = useState(false);
	const [suggestions, setSuggestions] = useState<string[]>([]);
	const inputRef = useRef<HTMLInputElement | null>(null);

	const tags = useMemo(() => splitTags(value), [value]);

	useEffect(() => {
		if (!contentTypeKey) return;
		let cancelled = false;
		client.content
			.apiContentTagsGet(contentTypeKey, fieldKey ?? "tags")
			.then((response) => {
				if (cancelled) return;
				const fetched = (response.data?.tags ?? []).filter((tag): tag is string => Boolean(tag && tag.trim()));
				setSuggestions(fetched);
			})
			.catch(() => {
				/* suggestions are optional : free text still works without them */
			});
		return () => {
			cancelled = true;
		};
	}, [contentTypeKey, fieldKey]);

	/** Append every non-empty, not-yet-present (case-insensitive) tag in one onChange call. */
	const commitTags = (incoming: string[]) => {
		const next = tags.slice();
		let changed = false;
		incoming.forEach((raw) => {
			const tag = raw.trim();
			if (!tag) return;
			if (next.some((existing) => existing.toLowerCase() === tag.toLowerCase())) return;
			next.push(tag);
			changed = true;
		});
		if (changed) onChange(joinTags(next));
	};

	const removeTag = (index: number) => {
		onChange(joinTags(tags.filter((_, i) => i !== index)));
	};

	// Typing a comma commits everything before it; the remainder stays in the input.
	const handleTextChange = (raw: string) => {
		if (!raw.includes(",")) {
			setText(raw);
			return;
		}
		const parts = raw.split(",");
		const remainder = parts.pop() ?? "";
		commitTags(parts);
		setText(remainder.trimStart());
	};

	const filteredSuggestions = useMemo(() => {
		const query = text.trim().toLowerCase();
		const selected = new Set(tags.map((tag) => tag.toLowerCase()));
		return suggestions.filter((suggestion) => {
			const normalized = suggestion.trim().toLowerCase();
			if (selected.has(normalized)) return false;
			return !query || normalized.includes(query);
		});
	}, [suggestions, tags, text]);

	const showSuggestions = focused && !disabled && filteredSuggestions.length > 0;

	return (
		<div className="space-y-1">
			{label && <span className="text-xs font-semibold text-slate-500">{label}</span>}
			<div className="relative">
				<div
					className={`flex flex-wrap items-center gap-1.5 rounded-xl border px-3 py-2 shadow-sm transition hover:border-slate-300 focus-within:border-[#005CA9] focus-within:ring-2 focus-within:ring-[#005CA9]/15 ${
						hasError ? "border-rose-300" : "border-slate-200"
					} ${disabled ? "bg-slate-50" : "bg-white"}`}
					onClick={() => inputRef.current?.focus()}
				>
					{tags.map((tag, index) => (
						<span
							key={`${tag}-${index}`}
							className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-700"
						>
							{tag}
							{!disabled && (
								<button
									type="button"
									onClick={(event) => {
										event.stopPropagation();
										removeTag(index);
									}}
									className="text-slate-400 transition hover:text-slate-700"
									aria-label={`Retirer ${tag}`}
								>
									<FiX className="h-3 w-3" />
								</button>
							)}
						</span>
					))}
					<input
						ref={inputRef}
						type="text"
						className="min-w-[140px] flex-1 border-none bg-transparent px-1 py-1 text-[15px] focus:outline-none disabled:cursor-not-allowed"
						placeholder="Ajouter un tag (Entrée ou virgule)…"
						value={text}
						onChange={(event) => handleTextChange(event.target.value)}
						onKeyDown={(event) => {
							if (event.key === "Enter") {
								event.preventDefault();
								commitTags([text]);
								setText("");
							}
						}}
						onFocus={() => setFocused(true)}
						onBlur={() => {
							if (text.trim()) commitTags([text]);
							setText("");
							setFocused(false);
						}}
						disabled={disabled}
					/>
				</div>
				{showSuggestions && (
					<div className="absolute left-0 right-0 top-full z-20 mt-1 max-h-48 overflow-auto rounded-lg border border-slate-200 bg-white py-1 shadow-lg">
						{filteredSuggestions.map((suggestion) => (
							<button
								key={suggestion}
								type="button"
								// preventDefault keeps focus on the input so blur doesn't double-commit the typed text.
								onMouseDown={(event) => event.preventDefault()}
								onClick={() => {
									commitTags([suggestion]);
									setText("");
								}}
								className="block w-full px-3 py-1.5 text-left text-sm text-slate-700 hover:bg-slate-50"
							>
								{suggestion}
							</button>
						))}
					</div>
				)}
			</div>
		</div>
	);
};

export default TagsInput;
